import { useEffect, useRef } from "react";

/**
 * Custom React hook that listens for clicks outside of specified elements and executes a callback function.
 *
 * @param {Function} onClick - Callback function to execute when a click occurs outside all referenced elements
 * @param {Array<React.RefObject>} [refs=[]] - Array of React ref objects pointing to elements to monitor for outside clicks
 *
 * @example
 * const mainDrawerRef = useRef(null);
 * const subDrawerRef = useRef(null);
 *
 * useOutsideClickListener(() => {
 *   setIsModalOpen(false);
 * }, [mainDrawerRef, subDrawerRef]);
 */
export const useOutsideClickListener = (onClick = () => null, refs = []) => {
  const callbackRef = useRef(onClick);

  // keep the callback ref up to date
  useEffect(() => {
    callbackRef.current = onClick;
  }, [onClick]);

  useEffect(() => {
    function handleClickOutside(event) {
      const isOutsideAllRefs = refs.every(
        (ref) => ref.current && !ref.current.contains(event.target)
      );

      if (isOutsideAllRefs) {
        callbackRef.current();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [refs.length]);
};
